/**
 * 页面加载指令
 * 用法: v-page-loading="isLoading"
 * 在目标容器上显示加载遮罩层
 */

import type { Directive, DirectiveBinding } from 'vue'
import loadingGif from '@/assets/images/loading.gif'

/** 预加载 loading 图，首次显示遮罩时已缓存 */
const preloadLoadingGif = (): void => {
  const img = new Image()
  img.src = loadingGif
}
preloadLoadingGif()

/**
 * 遮罩层的唯一标识属性名
 */
const LOADING_MASK_ATTR = 'data-page-loading-mask'

/**
 * 创建遮罩层元素
 */
const createMaskElement = (): HTMLElement => {
  const mask = document.createElement('div')
  mask.setAttribute(LOADING_MASK_ATTR, 'true')
  mask.className = 'fixed inset-0 flex flex-col items-center justify-center gap-2 bg-black/10 z-up-up'

  const img = document.createElement('img')
  img.src = loadingGif
  img.alt = ''
  img.className = 'w-16 h-16'

  const text = document.createElement('span')
  text.className = 'text-white text-base font-medium'
  text.textContent = 'Loading...'

  mask.appendChild(img)
  mask.appendChild(text)

  return mask
}

/**
 * 获取或设置元素的定位样式
 */
const ensurePosition = (el: HTMLElement) => {
  const position = getComputedStyle(el).position
  if (position === 'static') {
    el.style.position = 'relative'
  }
}

/**
 * 显示加载遮罩
 */
const showLoading = (el: HTMLElement) => {
  // 如果已经存在遮罩层，直接返回
  if (el.querySelector(`[${LOADING_MASK_ATTR}]`)) {
    return
  }

  // 确保元素有定位，以便遮罩层能正确覆盖
  ensurePosition(el)

  // 创建遮罩层
  const mask = createMaskElement()

  // 调整遮罩层样式，使其相对于目标容器定位
  mask.style.position = 'absolute'
  mask.style.top = '0'
  mask.style.left = '0'
  mask.style.right = '0'
  mask.style.bottom = '0'
  mask.style.width = '100%'
  mask.style.height = '100%'

  // 添加到目标容器
  el.appendChild(mask)
}

/**
 * 隐藏加载遮罩（移除页面内所有遮罩）
 */
const hideLoading = (_el: HTMLElement) => {
  document.querySelectorAll(`[${LOADING_MASK_ATTR}]`).forEach(mask => mask.remove())
}

/**
 * 页面加载指令
 * 用法: v-page-loading="isLoading"
 */
export const vPageLoading: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<boolean>) {
    if (binding.value) {
      showLoading(el)
    }
  },

  updated(el: HTMLElement, binding: DirectiveBinding<boolean>) {
    if (binding.value) {
      showLoading(el)
    } else {
      hideLoading(el)
    }
  },

  unmounted(el: HTMLElement) {
    hideLoading(el)
  },
}
